import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import SeoHead from "@/components/SeoHead";
import HeroBackground from "@/components/HeroBackground";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Fuel, ShieldCheck, MapPin, FileText, MessageSquare, IndianRupee, Clock,
  ArrowRight, Truck, CheckCircle2, Star, Menu, X, Users, Zap, Calendar,
  Package, Building2, Factory, HelpCircle, Plus, Smartphone, Cpu, CreditCard,
  Sparkles, Award, Phone, Mail, ArrowUp, Target, Eye, Heart, HardHat, Gauge,
  Facebook, Twitter, Instagram, Linkedin, Youtube, Send,
} from "lucide-react";

const ICONS = { Fuel, ShieldCheck, MapPin, FileText, MessageSquare, IndianRupee, Clock, Truck, Users, Zap, Calendar, Package, Building2, Factory, Smartphone, Cpu, CreditCard, Sparkles, Award, Target, Eye, Heart, HardHat, Gauge };

export default function Landing() {
  const CMS_CACHE_KEY = "fuelexpress_cms_cache_v1";
  const { data } = useQuery({
    queryKey: ["cms-landing"],
    queryFn: async () => {
      const r = await api.get("/cms/landing");
      try { localStorage.setItem(CMS_CACHE_KEY, JSON.stringify(r.data)); } catch { /* noop */ }
      return r.data;
    },
    initialData: () => {
      try { return JSON.parse(localStorage.getItem(CMS_CACHE_KEY) || "null") || undefined; }
      catch { return undefined; }
    },
  });
  const c = data?.content;
  const t = c?.theme || {};
  const cssVars = {
    "--pub-bg": t.background || "#FFFFFF",
    "--pub-surface": t.surface || "#F8FAFC",
    "--pub-surface-alt": t.surface_alt || "#FFF6EF",
    "--pub-primary": t.primary || "#F26722",
    "--pub-primary-hover": t.primary_hover || "#D9541A",
    "--pub-text": t.text || "#0B2447",
    "--pub-text-muted": t.text_muted || "#64748B",
    "--pub-border": t.border || "#E2E8F0",
    "--pub-dark": t.dark || "#0B2447",
    "--pub-success": t.success || "#10B981",
  };

  // Public landing must be light-themed regardless of admin dark mode toggle.
  useEffect(() => {
    document.documentElement.classList.remove("dark");
  }, []);

  if (!c) {
    return (
      <div className="min-h-screen bg-white text-slate-900 flex items-center justify-center">
        <div className="text-slate-400">Loading…</div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen antialiased"
      style={{ ...cssVars, backgroundColor: "var(--pub-bg)", color: "var(--pub-text)" }}
      data-testid="public-landing"
    >
      <SeoHead content={c} />
      <Header brand={c.brand} />
      <Hero hero={c.hero} />
      <TrustStrip metrics={c.trust_metrics || []} />
      <FiveAxis features={c.features || []} />
      <About about={c.about} />
      <Services services={c.services || []} />
      <Industries industries={c.industries || []} />
      <HowItWorks steps={c.how_it_works || []} />
      <Commitment items={c.commitment || []} />
      <Safety safety={c.safety} />
      <Benefits benefits={c.benefits || []} />
      <CoverageFaqRow coverage={c.coverage} faqItems={c.faq || []} />
      <Testimonials items={c.testimonials || []} />
      <AppDownload app={c.app_download} />
      <ContactCTA contact={c.contact} />
      <Footer footer={c.footer} brand={c.brand} social={c.social_media} />
      <BackToTop />
    </div>
  );
}

/* ─────────── Back to top ─────────── */
function BackToTop() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const on = () => setVisible(window.scrollY > 480);
    window.addEventListener("scroll", on, { passive: true });
    on();
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <button
      type="button"
      aria-label="Back to top"
      data-testid="btn-back-to-top"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className={`fixed bottom-6 right-6 z-40 flex h-12 w-12 items-center justify-center rounded-full shadow-xl ring-1 ring-black/5 transition-all duration-300 hover:scale-110 hover:shadow-2xl ${
        visible ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-4 opacity-0"
      }`}
      style={{
        backgroundColor: "var(--pub-primary)",
        color: "#fff",
      }}
    >
      <ArrowUp size={20} strokeWidth={2.5} />
    </button>
  );
}

/* ─────────── Header ─────────── */
function Header({ brand }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <header
      className={`sticky top-0 z-50 border-b transition-all ${
        scrolled ? "bg-white/95 shadow-sm backdrop-blur-md" : "bg-white/70 backdrop-blur"
      }`}
      style={{ borderColor: "var(--pub-border)" }}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6">
        <Link
          to="/"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2.5"
          data-testid="brand-logo-home"
        >
          {brand?.logo_url ? (
            <img
              src={brand.logo_url}
              alt={brand?.name || "Logo"}
              className="h-11 w-11 rounded-2xl object-contain bg-white p-1 shadow-lg ring-1 ring-black/5"
              data-testid="brand-logo-image"
            />
          ) : (
            <div className="grid h-11 w-11 place-items-center rounded-2xl bg-[var(--pub-primary)] text-white shadow-lg shadow-[var(--pub-primary)]/25">
              <Fuel size={20} strokeWidth={2.5} />
            </div>
          )}
          <div>
            <div className="font-display text-lg font-black tracking-tight text-[var(--pub-dark)]" data-testid="brand-name">
              {brand?.name || "FuelExpress"}
            </div>
            {brand?.tagline && (
              <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-widest text-[var(--pub-text-muted)]" data-testid="brand-tagline">
                {brand.tagline}
              </div>
            )}
          </div>
        </Link>
        <nav className="hidden items-center gap-7 md:flex">
          {[
            { h: "#about", l: "About" },
            { h: "#services", l: "Solutions" },
            { h: "#industries", l: "Industries" },
            { h: "#how", l: "How it works" },
            { h: "#faq", l: "FAQ" },
            { h: "#contact", l: "Contact" },
          ].map((n) => (
            <a
              key={n.h}
              href={n.h}
              className="text-sm font-medium text-[var(--pub-text-muted)] transition-colors hover:text-[var(--pub-primary)]"
            >
              {n.l}
            </a>
          ))}
        </nav>
        <div className="hidden items-center gap-3 md:flex">
          <Link to="/login" data-testid="public-nav-login">
            <Button variant="ghost" className="rounded-full text-[var(--pub-dark)] hover:bg-[var(--pub-surface)]">
              Client Login
            </Button>
          </Link>
          <a href="#contact">
            <Button
              className="rounded-full bg-[var(--pub-primary)] px-6 font-semibold text-white shadow-lg shadow-[var(--pub-primary)]/30 hover:bg-[var(--pub-primary-hover)]"
              data-testid="public-nav-cta"
            >
              Fuel Up Now <ArrowRight size={14} className="ml-1.5" />
            </Button>
          </a>
        </div>
        <button className="md:hidden text-[var(--pub-dark)]" onClick={() => setOpen(!open)} aria-label="menu">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="border-t bg-white px-6 py-4 md:hidden" style={{ borderColor: "var(--pub-border)" }}>
          {[
            { h: "#about", l: "About" },
            { h: "#services", l: "Solutions" },
            { h: "#industries", l: "Industries" },
            { h: "#how", l: "How it works" },
            { h: "#faq", l: "FAQ" },
            { h: "#contact", l: "Contact" },
          ].map((n) => (
            <a key={n.h} href={n.h} className="block py-2 text-sm font-medium text-[var(--pub-dark)]" onClick={() => setOpen(false)}>
              {n.l}
            </a>
          ))}
          <Link to="/login" className="block py-2 text-sm font-semibold text-[var(--pub-primary)]">
            Client Login →
          </Link>
        </div>
      )}
    </header>
  );
}

/* ─────────── Hero ─────────── */
// Public endpoint returns pre-resolved values (mix of live+manual per admin
// config). The client is a pure renderer — no source logic here.
function useLiveWidget() {
  const { data } = useQuery({
    queryKey: ["public-live-stats"],
    queryFn: () => api.get("/public/live-stats").then((r) => r.data),
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    staleTime: 10_000,
  });
  return data;
}

// Compact number formatter — 1234 → "1.2K", 1230000 → "1.2M".
function fmtCompact(n) {
  if (n == null || Number.isNaN(n)) return "—";
  const num = Number(n);
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
  return Math.round(num).toString();
}

function Hero({ hero }) {
  const w = useLiveWidget();
  const snapshot = [
    { l: "Litres Today", v: w ? fmtCompact(w.litres_today)     : "—" },
    { l: "Bowsers",      v: w ? String(w.bowsers)              : "—" },
    { l: "Cities",       v: w ? String(w.cities)               : "—" },
    { l: "Deliveries",   v: w ? String(w.deliveries_today)     : "—" },
  ];
  const monthlyBadge = w && Number(w.monthly_litres) > 0
    ? `${fmtCompact(w.monthly_litres)} L / mo`
    : "1.2M+ L / mo";
  return (
    <section className="relative isolate overflow-hidden bg-[var(--pub-surface-alt)]">
      {/* Soft decorative orbs */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full opacity-30 blur-3xl"
        style={{ backgroundColor: "var(--pub-primary)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-32 top-1/3 h-96 w-96 rounded-full opacity-20 blur-3xl"
        style={{ backgroundColor: "var(--pub-dark)" }}
      />
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 pb-24 pt-10 lg:grid-cols-2 lg:pb-32 lg:pt-14">
        <div>
          <div
            className="mb-6 inline-flex items-center gap-2 rounded-full border bg-white/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider backdrop-blur"
            style={{ borderColor: "var(--pub-border)", color: "var(--pub-primary)" }}
          >
            <Sparkles size={12} /> {hero?.overline || "DOORSTEP DIESEL DELIVERY"}
          </div>
          <h1 className="whitespace-pre-line font-display text-4xl font-black leading-[1.05] tracking-tight text-[var(--pub-dark)] sm:text-5xl md:text-6xl lg:text-7xl">
            {(hero?.headline || "").split("\n").map((line, i) => (
              <span key={`hero-line-${i}`} className={i === 1 ? "block text-[var(--pub-primary)]" : "block"}>
                {line}
              </span>
            ))}
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-[var(--pub-text-muted)]">
            {hero?.subheadline}
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <a href={hero?.cta_primary_href || "#contact"}>
              <Button
                size="lg"
                className="rounded-full border-2 border-transparent bg-[var(--pub-primary)] px-8 py-6 font-semibold text-white shadow-xl shadow-[var(--pub-primary)]/30 hover:bg-[var(--pub-primary-hover)]"
                data-testid="public-hero-cta-quote"
              >
                {hero?.cta_primary_label || "Fuel Up Now"} <ArrowRight size={16} className="ml-2" />
              </Button>
            </a>
            <a href={hero?.cta_secondary_href || "#coverage"}>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full border-2 bg-white/60 px-8 py-6 font-semibold text-[var(--pub-dark)] backdrop-blur hover:bg-white"
                style={{ borderColor: "var(--pub-dark)" }}
                data-testid="public-hero-cta-coverage"
              >
                {hero?.cta_secondary_label || "Explore Solutions"}
              </Button>
            </a>
          </div>
          {/* Quick trust badges */}
          <div className="mt-10 flex flex-wrap items-center gap-6 text-xs font-medium text-[var(--pub-text-muted)]">
            <div className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-[var(--pub-primary)]" /> PESO Approved</div>
            <div className="flex items-center gap-1.5"><Clock size={14} className="text-[var(--pub-primary)]" /> 24×7 Delivery</div>
            <div className="flex items-center gap-1.5"><Award size={14} className="text-[var(--pub-primary)]" /> BS-VI HSD</div>
          </div>
        </div>
        {/* Right visual */}
        <div className="relative">
          <div
            className="relative overflow-hidden rounded-[2.5rem] border bg-white shadow-2xl"
            style={{ borderColor: "var(--pub-border)" }}
          >
            <div className="relative h-[26rem] w-full md:h-[32rem]">
              <HeroBackground hero={hero} />
            </div>
            {/* Floating live snapshot card */}
            <div className="absolute bottom-6 left-6 right-6 rounded-2xl bg-white/95 p-5 shadow-xl backdrop-blur">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-xs font-bold uppercase tracking-widest text-[var(--pub-dark)]">Live snapshot</div>
                <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                  </span>
                  ACTIVE
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                {snapshot.map((s) => (
                  <div key={s.l} className="text-center" data-testid={`live-stat-${s.l.toLowerCase().replace(/\s+/g,'-')}`}>
                    <div className="font-display text-xl font-black text-[var(--pub-primary)] tabular-nums transition-all duration-500">{s.v}</div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--pub-text-muted)]">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* Floating badge — desktop/tablet only (hidden on mobile) */}
          <div className="absolute -right-4 -top-4 hidden rounded-2xl bg-[var(--pub-primary)] px-5 py-4 text-white shadow-xl md:block" data-testid="live-badge-monthly">
            <div className="text-xs font-semibold uppercase tracking-wider opacity-90">Delivered</div>
            <div className="font-display text-2xl font-black tabular-nums">{monthlyBadge}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────── Trust Strip ─────────── */
function TrustStrip({ metrics }) {
  if (!metrics.length) return null;
  return (
    <section className="border-y bg-white py-14" style={{ borderColor: "var(--pub-border)" }}>
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-8 text-center text-xs font-bold uppercase tracking-[0.3em] text-[var(--pub-text-muted)]">
          Trusted by India&apos;s builders, factories & fleet operators
        </div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {metrics.map((m) => (
            <div
              key={m.label}
              className="rounded-2xl border bg-[var(--pub-surface)] p-6 text-center transition-all hover:-translate-y-0.5 hover:border-[var(--pub-primary)] hover:shadow-lg"
              style={{ borderColor: "var(--pub-border)" }}
              data-testid={`public-metric-${m.label}`}
            >
              <div className="font-display text-3xl font-black text-[var(--pub-primary)] md:text-4xl">{m.value}</div>
              <div className="mt-2 text-xs font-semibold uppercase tracking-widest text-[var(--pub-text-muted)]">{m.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────── Five-Axis / Features ─────────── */
function FiveAxis({ features }) {
  if (!features.length) return null;
  return (
    <section id="features" className="bg-white py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-16 text-center">
          <div className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-[var(--pub-primary)]">Why choose us</div>
          <h2 className="mx-auto max-w-3xl font-display text-3xl font-black tracking-tight text-[var(--pub-dark)] md:text-5xl">
            Five-axis, tech-centred fuel delivery.
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-[var(--pub-text-muted)]">
            Our approach revolves around technology as the core enabler — seamlessly connecting convenience, cashless
            transactions, quality &amp; quantity, and safety.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {features.slice(0, 8).map((f) => {
            const Icon = ICONS[f.icon] || Fuel;
            return (
              <div
                key={f.title}
                className="group relative overflow-hidden rounded-3xl border bg-white p-6 transition-all hover:-translate-y-1 hover:border-[var(--pub-primary)] hover:shadow-2xl hover:shadow-[var(--pub-primary)]/10"
                style={{ borderColor: "var(--pub-border)" }}
                data-testid="public-feature-card"
              >
                <div
                  aria-hidden
                  className="absolute -right-8 -top-8 h-32 w-32 rounded-full opacity-0 blur-3xl transition-opacity group-hover:opacity-20"
                  style={{ backgroundColor: "var(--pub-primary)" }}
                />
                <div className="relative mb-5 inline-grid h-14 w-14 place-items-center rounded-2xl bg-[var(--pub-surface-alt)] text-[var(--pub-primary)] transition-colors group-hover:bg-[var(--pub-primary)] group-hover:text-white">
                  <Icon size={24} strokeWidth={2} />
                </div>
                <h3 className="relative font-display text-lg font-black tracking-tight text-[var(--pub-dark)]">{f.title}</h3>
                <p className="relative mt-2 text-sm leading-relaxed text-[var(--pub-text-muted)]">{f.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─────────── About · Mission · Vision · Core Values ─────────── */
function About({ about }) {
  if (!about) return null;
  const values = about.core_values || [];
  const short = about.short_mission || about.short_vision;
  return (
    <section id="about" className="bg-[var(--pub-surface)] py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-6">
        {/* Section eyebrow + headline */}
        <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <div className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-[var(--pub-primary)]">
              {about.eyebrow || "About"}
            </div>
            {about.headline ? (
              <h2 className="font-display text-3xl font-black leading-[1.05] tracking-tight text-[var(--pub-dark)] md:text-5xl">
                {about.headline}
              </h2>
            ) : null}
            {about.brand_statement ? (
              <div
                className="mt-6 inline-flex items-center gap-2 rounded-full border bg-white px-4 py-2 text-xs font-bold uppercase tracking-widest text-[var(--pub-primary)]"
                style={{ borderColor: "var(--pub-primary)" }}
                data-testid="about-brand-statement"
              >
                <Fuel size={12} /> {about.brand_statement}
              </div>
            ) : null}
          </div>
          <div className="lg:col-span-3 space-y-4">
            {about.body ? (
              <p className="text-lg leading-relaxed text-[var(--pub-text)]">{about.body}</p>
            ) : null}
            {about.description ? (
              <p className="text-base leading-relaxed text-[var(--pub-text)]/85">{about.description}</p>
            ) : null}
          </div>
        </div>

        {/* Mission & Vision — side-by-side highlight cards */}
        {(about.mission || about.vision) && (
          <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2" data-testid="about-mission-vision">
            {about.mission ? (
              <article
                className="group relative overflow-hidden rounded-2xl bg-white p-8 shadow-[0_20px_60px_-40px_rgba(0,0,0,0.4)] ring-1 ring-black/5 transition hover:-translate-y-1 hover:shadow-[0_30px_80px_-40px_rgba(0,0,0,0.5)]"
                data-testid="about-mission"
              >
                <div className="absolute -right-6 -top-6 h-32 w-32 rounded-full bg-[var(--pub-primary)]/10 blur-2xl" />
                <div className="relative">
                  <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--pub-primary)]/10 text-[var(--pub-primary)]">
                    <Target size={22} />
                  </div>
                  <div className="mb-1 text-xs font-bold uppercase tracking-[0.25em] text-[var(--pub-primary)]">Our Mission</div>
                  {about.short_mission ? (
                    <blockquote className="mb-3 border-l-2 pl-3 font-display text-lg italic text-[var(--pub-dark)]" style={{ borderColor: "var(--pub-primary)" }}>
                      &ldquo;{about.short_mission}&rdquo;
                    </blockquote>
                  ) : null}
                  <p className="text-sm leading-relaxed text-[var(--pub-text)]">{about.mission}</p>
                </div>
              </article>
            ) : null}
            {about.vision ? (
              <article
                className="group relative overflow-hidden rounded-2xl bg-[var(--pub-dark)] p-8 text-white shadow-[0_20px_60px_-40px_rgba(0,0,0,0.4)] ring-1 ring-white/10 transition hover:-translate-y-1"
                data-testid="about-vision"
              >
                <div className="absolute -right-6 -top-6 h-32 w-32 rounded-full bg-[var(--pub-primary)]/25 blur-2xl" />
                <div className="relative">
                  <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 text-white">
                    <Eye size={22} />
                  </div>
                  <div className="mb-1 text-xs font-bold uppercase tracking-[0.25em] text-white/70">Our Vision</div>
                  {about.short_vision ? (
                    <blockquote className="mb-3 border-l-2 border-white/40 pl-3 font-display text-lg italic text-white">
                      &ldquo;{about.short_vision}&rdquo;
                    </blockquote>
                  ) : null}
                  <p className="text-sm leading-relaxed text-white/85">{about.vision}</p>
                </div>
              </article>
            ) : null}
          </div>
        )}

        {/* Core values grid */}
        {values.length > 0 && (
          <div className="mt-16" data-testid="about-core-values">
            <div className="mb-6 flex items-end justify-between gap-4">
              <div>
                <div className="mb-2 text-xs font-bold uppercase tracking-[0.3em] text-[var(--pub-primary)]">What we stand for</div>
                <h3 className="font-display text-2xl font-black tracking-tight text-[var(--pub-dark)] md:text-3xl">Core values</h3>
              </div>
              {short ? (
                <div className="hidden max-w-md text-right text-sm italic text-[var(--pub-text)]/70 md:block">
                  &ldquo;{short}&rdquo;
                </div>
              ) : null}
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {values.map((v, i) => {
                const Icon = ICONS[v.icon] || Sparkles;
                return (
                  <div
                    key={`${v.title}-${i}`}
                    className="group rounded-xl border border-black/5 bg-white p-5 transition hover:-translate-y-0.5 hover:border-[var(--pub-primary)]/40 hover:shadow-md"
                    data-testid={`about-core-value-${i}`}
                  >
                    <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--pub-primary)]/10 text-[var(--pub-primary)] transition group-hover:bg-[var(--pub-primary)] group-hover:text-white">
                      <Icon size={18} />
                    </div>
                    <div className="font-display text-base font-bold text-[var(--pub-dark)]">{v.title}</div>
                    {v.description ? (
                      <p className="mt-1 text-sm leading-relaxed text-[var(--pub-text)]/85">{v.description}</p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/* ─────────── Services ─────────── */
function Services({ services }) {
  if (!services.length) return null;
  return (
    <section id="services" className="bg-white py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-14 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div>
            <div className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-[var(--pub-primary)]">Our Solutions</div>
            <h2 className="max-w-2xl font-display text-3xl font-black tracking-tight text-[var(--pub-dark)] md:text-5xl">
              Complete fuel-supply solutions for every industry.
            </h2>
          </div>
          <a
            href="#contact"
            className="inline-flex items-center gap-1.5 rounded-full border-2 border-[var(--pub-dark)] px-5 py-2.5 text-sm font-semibold text-[var(--pub-dark)] transition-colors hover:border-[var(--pub-primary)] hover:text-[var(--pub-primary)]"
          >
            Get a quote <ArrowRight size={14} />
          </a>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {services.map((s) => {
            const Icon = ICONS[s.icon] || Fuel;
            return (
              <div
                key={s.title}
                className="group flex flex-col overflow-hidden rounded-3xl border bg-white transition-all hover:-translate-y-1 hover:shadow-2xl"
                style={{ borderColor: "var(--pub-border)" }}
                data-testid="public-service-card"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-[var(--pub-surface-alt)]">
                  {s.image_url ? (
                    <img
                      src={s.image_url}
                      alt={s.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  ) : (
                    <div className="grid h-full w-full place-items-center bg-gradient-to-br from-[var(--pub-primary)]/15 to-[var(--pub-dark)]/10">
                      <Icon size={56} className="text-[var(--pub-primary)]" strokeWidth={1.5} />
                    </div>
                  )}
                  <div className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full bg-white/95 text-[var(--pub-primary)] shadow-md">
                    <Icon size={18} strokeWidth={2} />
                  </div>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-display text-lg font-black leading-tight tracking-tight text-[var(--pub-dark)]">
                    {s.title}
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-[var(--pub-text-muted)]">{s.description}</p>
                  <a
                    href="#contact"
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--pub-primary)] transition-transform hover:translate-x-1"
                  >
                    Explore more <ArrowRight size={14} />
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─────────── Industries ─────────── */
function Industries({ industries }) {
  if (!industries.length) return null;
  return (
    <section id="industries" className="bg-[var(--pub-surface)] py-24 md:py-28">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-10 text-center">
          <div className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-[var(--pub-primary)]">Industries We Serve</div>
          <h2 className="font-display text-3xl font-black tracking-tight text-[var(--pub-dark)] md:text-5xl">
            From power plants to ports.
          </h2>
        </div>
        <div className="flex flex-wrap justify-center gap-2.5">
          {industries.map((name) => (
            <div
              key={name}
              className="rounded-full border bg-white px-5 py-2 text-sm font-medium text-[var(--pub-dark)] shadow-sm transition-all hover:-translate-y-0.5 hover:border-[var(--pub-primary)] hover:text-[var(--pub-primary)] hover:shadow-md"
              style={{ borderColor: "var(--pub-border)" }}
              data-testid="public-industry-chip"
            >
              {name}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────── How it works ─────────── */
function HowItWorks({ steps }) {
  if (!steps.length) return null;
  return (
    <section id="how" className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-16 text-center">
          <div className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-[var(--pub-primary)]">The Process</div>
          <h2 className="font-display text-3xl font-black tracking-tight text-[var(--pub-dark)] md:text-5xl">
            Order to invoice in {steps.length} steps
          </h2>
        </div>
        <div className="relative grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <div
            className="absolute left-0 right-0 top-10 hidden h-0.5 xl:block"
            style={{ backgroundImage: "linear-gradient(to right, transparent, var(--pub-primary), transparent)" }}
          />
          {steps.map((s) => (
            <div key={`step-${s.step}`} className="relative flex flex-col items-center text-center">
              <div className="relative z-10 mb-5 grid h-20 w-20 place-items-center rounded-full bg-white shadow-xl ring-4 ring-[var(--pub-primary)]/15">
                <div className="grid h-14 w-14 place-items-center rounded-full bg-[var(--pub-primary)] font-display text-2xl font-black text-white">
                  {s.step}
                </div>
              </div>
              <h3 className="font-display text-base font-black tracking-tight text-[var(--pub-dark)]">{s.title}</h3>
              <p className="mt-2 text-sm text-[var(--pub-text-muted)]">{s.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────── Commitment ─────────── */
function Commitment({ items }) {
  if (!items.length) return null;
  return (
    <section className="bg-[var(--pub-surface)] py-20">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-10">
          <div className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-[var(--pub-primary)]">Our Commitment</div>
          <h2 className="font-display text-3xl font-black tracking-tight text-[var(--pub-dark)] md:text-4xl">
            What every customer gets — always.
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {items.map((it) => (
            <div
              key={it}
              className="flex items-start gap-3 rounded-2xl border bg-white p-4 transition-all hover:border-[var(--pub-primary)]"
              style={{ borderColor: "var(--pub-border)" }}
            >
              <div className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-full bg-[var(--pub-primary)]/15 text-[var(--pub-primary)]">
                <CheckCircle2 size={16} />
              </div>
              <span className="text-sm font-medium text-[var(--pub-text)]">{it}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────── Safety ─────────── */
function Safety({ safety }) {
  if (!safety) return null;
  return (
    <section id="safety" className="bg-white py-24 md:py-32">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 lg:grid-cols-2">
        <div className="lg:sticky lg:top-24">
          <div className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-[var(--pub-primary)]">Safety &amp; Quality</div>
          <h2 className="font-display text-3xl font-black leading-[1.05] tracking-tight text-[var(--pub-dark)] md:text-5xl">
            {safety.headline}
          </h2>
          <p className="mt-4 max-w-md text-[var(--pub-text-muted)]">{safety.subtitle}</p>
          {safety.footnote && (
            <p className="mt-6 max-w-md text-sm italic text-[var(--pub-text-muted)]">{safety.footnote}</p>
          )}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {(safety.items || []).map((it) => (
            <div
              key={it}
              className="flex items-center gap-3 rounded-2xl border bg-[var(--pub-surface)] p-4 transition-all hover:border-[var(--pub-primary)] hover:bg-white"
              style={{ borderColor: "var(--pub-border)" }}
            >
              <ShieldCheck size={18} className="flex-shrink-0 text-[var(--pub-primary)]" />
              <span className="text-sm font-medium text-[var(--pub-text)]">{it}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────── Benefits ─────────── */
function Benefits({ benefits }) {
  if (!benefits.length) return null;
  return (
    <section className="bg-[var(--pub-surface)] py-20">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-10 text-center">
          <div className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-[var(--pub-primary)]">Benefits</div>
          <h2 className="font-display text-3xl font-black tracking-tight text-[var(--pub-dark)] md:text-4xl">
            What doorstep delivery unlocks.
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {benefits.map((b) => (
            <div
              key={b}
              className="flex items-center gap-3 rounded-2xl border bg-white p-5"
              style={{ borderColor: "var(--pub-border)" }}
            >
              <div className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-full bg-[var(--pub-primary)]/12 text-[var(--pub-primary)]">
                <CheckCircle2 size={16} />
              </div>
              <span className="text-sm font-medium leading-snug text-[var(--pub-text)]">{b}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────── Coverage + FAQ (side-by-side on desktop) ─────────── */
function CoverageFaqRow({ coverage, faqItems }) {
  const [openIdx, setOpenIdx] = useState(0);
  if (!coverage && !(faqItems && faqItems.length)) return null;
  return (
    <section id="coverage" className="bg-white py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-2 lg:gap-16">
          {/* Left: Coverage */}
          {coverage && (
            <div>
              <div className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-[var(--pub-primary)]">Coverage</div>
              <h2 className="font-display text-3xl font-black leading-[1.05] tracking-tight text-[var(--pub-dark)] md:text-4xl">
                {coverage.title}
              </h2>
              <p className="mt-4 max-w-md text-[var(--pub-text-muted)]">{coverage.subtitle}</p>
              {coverage.map_image_url && (
                <div
                  className="relative mt-8 aspect-[16/10] overflow-hidden rounded-3xl border bg-[var(--pub-surface)]"
                  style={{ borderColor: "var(--pub-border)" }}
                >
                  <img src={coverage.map_image_url} alt="Coverage map" className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-white/40 via-transparent to-transparent" />
                </div>
              )}
              <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {(coverage.cities || []).map((city) => (
                  <div
                    key={city}
                    className="flex items-center gap-2 rounded-lg border bg-[var(--pub-surface)] px-3 py-2 transition-all hover:border-[var(--pub-primary)] hover:bg-white"
                    style={{ borderColor: "var(--pub-border)" }}
                  >
                    <MapPin size={12} className="text-[var(--pub-primary)]" />
                    <span className="text-sm font-medium text-[var(--pub-text)]">{city}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Right: FAQ */}
          {faqItems && faqItems.length > 0 && (
            <div id="faq">
              <div className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-[var(--pub-primary)]">FAQ</div>
              <h2 className="font-display text-3xl font-black tracking-tight text-[var(--pub-dark)] md:text-4xl">
                Frequently asked questions
              </h2>
              <div className="mt-8 space-y-3">
                {faqItems.map((q, i) => {
                  const isOpen = openIdx === i;
                  return (
                    <div
                      key={q.question}
                      className={`overflow-hidden rounded-2xl border bg-white transition-all ${isOpen ? "shadow-lg" : ""}`}
                      style={{ borderColor: isOpen ? "var(--pub-primary)" : "var(--pub-border)" }}
                      data-testid="public-faq-item"
                    >
                      <button
                        onClick={() => setOpenIdx(isOpen ? -1 : i)}
                        aria-expanded={isOpen}
                        aria-controls={`faq-panel-${i}`}
                        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-[var(--pub-surface)]"
                      >
                        <span className="flex items-center gap-3 font-display text-sm font-bold tracking-tight text-[var(--pub-dark)]">
                          <HelpCircle size={16} className="text-[var(--pub-primary)]" /> {q.question}
                        </span>
                        <span
                          className={`grid h-8 w-8 flex-shrink-0 place-items-center rounded-full text-white transition-all ${
                            isOpen ? "rotate-45 bg-[var(--pub-primary)]" : "bg-[var(--pub-dark)]"
                          }`}
                        >
                          <Plus size={16} />
                        </span>
                      </button>
                      {isOpen && (
                        <div
                          id={`faq-panel-${i}`}
                          className="border-t px-5 py-4 text-sm leading-relaxed text-[var(--pub-text-muted)]"
                          style={{ borderColor: "var(--pub-border)" }}
                        >
                          {q.answer}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/* ─────────── Testimonials ─────────── */
function Testimonials({ items }) {
  if (!items.length) return null;
  return (
    <section className="bg-[var(--pub-surface)] py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-10 text-center">
          <div className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-[var(--pub-primary)]">What customers say</div>
          <h2 className="mx-auto max-w-2xl font-display text-3xl font-black tracking-tight text-[var(--pub-dark)] md:text-5xl">
            Zero downtime. Real numbers.
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {items.map((tm, i) => (
            <div
              key={`testimonial-${i}-${tm.name}`}
              className="relative flex flex-col rounded-3xl border bg-white p-8 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
              style={{ borderColor: "var(--pub-border)" }}
            >
              <div className="mb-4 flex gap-1 text-[var(--pub-primary)]">
                {[0, 1, 2, 3, 4].map((s) => (
                  <Star key={s} size={16} fill="currentColor" strokeWidth={0} />
                ))}
              </div>
              <p className="flex-1 text-base leading-relaxed text-[var(--pub-text)]">
                <span className="font-display text-3xl leading-none text-[var(--pub-primary)]">“</span>
                {tm.quote}
                <span className="font-display text-3xl leading-none text-[var(--pub-primary)]">”</span>
              </p>
              <div className="mt-6 flex items-center gap-3 border-t pt-6" style={{ borderColor: "var(--pub-border)" }}>
                {tm.image_url ? (
                  <img
                    src={tm.image_url}
                    alt={tm.name}
                    className="h-11 w-11 rounded-full border object-cover"
                    style={{ borderColor: "var(--pub-border)" }}
                  />
                ) : (
                  <div
                    className="grid h-11 w-11 place-items-center rounded-full bg-[var(--pub-surface)] text-sm font-bold text-[var(--pub-dark)]"
                    style={{ borderColor: "var(--pub-border)" }}
                  >
                    {tm.name?.[0]}
                  </div>
                )}
                <div>
                  <div className="font-semibold text-[var(--pub-dark)]">{tm.name}</div>
                  <div className="text-xs text-[var(--pub-text-muted)]">{tm.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────── App Download ─────────── */
function AppleLogo({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.05 12.04c-.03-2.72 2.22-4.02 2.32-4.09-1.27-1.85-3.24-2.11-3.94-2.13-1.68-.17-3.28.99-4.13.99-.86 0-2.17-.97-3.57-.94-1.84.03-3.53 1.07-4.48 2.71-1.91 3.32-.49 8.23 1.37 10.93.91 1.32 1.99 2.8 3.4 2.75 1.37-.06 1.89-.89 3.55-.89s2.12.89 3.57.86c1.47-.03 2.4-1.35 3.3-2.68 1.04-1.53 1.47-3.02 1.49-3.1-.03-.02-2.86-1.1-2.89-4.4zM14.38 4.05c.76-.92 1.27-2.2 1.13-3.47-1.09.04-2.41.72-3.19 1.64-.7.81-1.32 2.11-1.15 3.36 1.22.09 2.46-.62 3.21-1.53z"/>
    </svg>
  );
}

function GooglePlayLogo({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 512 512" aria-hidden="true">
      <path fill="#EA4335" d="M337.4 254.5 61.5 511.9c1.6.4 3.3.6 5 .6 4.4 0 8.4-1.4 11.9-3.4l305.8-176.7-46.8-77.9z"/>
      <path fill="#FBBC04" d="M486.6 218.2 384.2 158.7 337.4 254.5l89.7 89.7 59.5-34.3c15.9-9.2 15.9-32.3 0-41.4z"/>
      <path fill="#4285F4" d="M78.4 3.5C74.9 1.4 70.9 0 66.5 0c-1.7 0-3.4.2-5 .6l276 257.3L384.2 158.7 78.4 3.5z"/>
      <path fill="#34A853" d="M61.5 511.9V.6C55.8 3.1 51.5 8.5 51.5 15.4v482.4c0 6.9 4.3 12.3 10 14.1z"/>
    </svg>
  );
}

function AppDownload({ app }) {
  if (!app || app.enabled === false) return null;
  return (
    <section className="relative isolate overflow-hidden bg-[var(--pub-dark)] py-14 md:py-16">
      <div
        aria-hidden
        className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full opacity-30 blur-3xl"
        style={{ backgroundColor: "var(--pub-primary)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full opacity-20 blur-3xl"
        style={{ backgroundColor: "var(--pub-primary)" }}
      />
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 md:grid-cols-2">
        <div>
          <div className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-[var(--pub-primary)]">Download Now</div>
          <h2 className="font-display text-4xl font-black leading-[1.05] tracking-tight text-white md:text-5xl">
            {app.headline || "Upgrade your refueling experience."}
          </h2>
          <p className="mt-4 max-w-md text-base text-white/80">
            {app.subheadline ||
              "Download now to order diesel at your doorstep. We're here to make procurement smooth and smarter."}
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a
              href={app.ios_url || "#"}
              className="group flex items-center gap-3 rounded-2xl bg-black px-5 py-3 text-white ring-1 ring-white/10 transition-all hover:-translate-y-0.5 hover:bg-white hover:text-black hover:shadow-2xl"
              data-testid="btn-app-store"
            >
              <AppleLogo size={26} />
              <div className="text-left">
                <div className="text-[10px] font-semibold uppercase tracking-widest opacity-70">Download on the</div>
                <div className="font-display text-base font-black leading-tight">App Store</div>
              </div>
            </a>
            <a
              href={app.android_url || "#"}
              className="group flex items-center gap-3 rounded-2xl bg-black px-5 py-3 text-white ring-1 ring-white/10 transition-all hover:-translate-y-0.5 hover:bg-white hover:text-black hover:shadow-2xl"
              data-testid="btn-google-play"
            >
              <GooglePlayLogo size={26} />
              <div className="text-left">
                <div className="text-[10px] font-semibold uppercase tracking-widest opacity-70">Get it on</div>
                <div className="font-display text-base font-black leading-tight">Google Play</div>
              </div>
            </a>
          </div>
        </div>
        <div className="relative mx-auto flex items-center justify-center">
          {app.mockup_image_url ? (
            <img src={app.mockup_image_url} alt="App preview" className="max-h-[30rem] w-auto object-contain drop-shadow-2xl" />
          ) : (
            <AppMockupFrame />
          )}
        </div>
      </div>
    </section>
  );
}

function AppMockupFrame({ variant }) {
  const isBack = variant === "back";
  return (
    <div className="relative">
      {/* Soft glow ring behind device — hide on 'back' variant to avoid overlapping halos */}
      {!isBack && (
        <div
          aria-hidden
          className="absolute -inset-8 rounded-[3rem] opacity-60 blur-3xl"
          style={{ background: "radial-gradient(circle, var(--pub-primary) 0%, transparent 70%)" }}
        />
      )}
      {/* Device chassis */}
      <div
        className="relative overflow-hidden rounded-[2.75rem] border-[6px] border-white/15 bg-gradient-to-br from-slate-900 to-slate-800 shadow-2xl"
        style={{ width: 220, height: 440 }}
      >
        {/* Notch */}
        <div className="absolute left-1/2 top-2 z-20 h-5 w-24 -translate-x-1/2 rounded-b-2xl bg-black/90" />
        {/* Screen content */}
        <div className="relative flex h-full flex-col p-4 text-white">
          {/* Status bar */}
          <div className="flex items-center justify-between text-[10px] font-semibold text-white/80">
            <span>9:41</span>
            <div className="flex items-center gap-1">
              <span>●●●</span>
              <span>▲</span>
              <span className="rounded border border-white/40 px-1 text-[8px]">92%</span>
            </div>
          </div>

          {/* App header */}
          <div className="mt-6 flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-xl bg-[var(--pub-primary)] shadow-lg">
              <Fuel size={16} strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-xs font-black">FuelExpress</div>
              <div className="text-[9px] text-white/60">Welcome back, Rakesh</div>
            </div>
          </div>

          {/* Hero balance card */}
          <div className="mt-4 rounded-2xl bg-gradient-to-br from-[var(--pub-primary)] to-[color-mix(in_srgb,var(--pub-primary)_60%,black)] p-3 shadow-lg">
            <div className="text-[9px] font-semibold uppercase tracking-widest text-white/80">Wallet</div>
            <div className="font-display text-2xl font-black leading-tight">₹ 48,250</div>
            <div className="mt-1 flex items-center justify-between text-[9px] text-white/80">
              <span>+₹ 5,000 today</span>
              <span className="rounded-full bg-white/20 px-2 py-0.5 font-semibold">Add funds</span>
            </div>
          </div>

          {/* Quick actions */}
          <div className="mt-3 grid grid-cols-3 gap-2">
            {[
              { l: "Book", i: Truck },
              { l: "Track", i: MapPin },
              { l: "Invoice", i: FileText },
            ].map(({ l, i: Icon }) => (
              <div key={l} className="rounded-xl bg-white/10 p-2 text-center">
                <Icon size={14} className="mx-auto text-[var(--pub-primary)]" />
                <div className="mt-1 text-[9px] font-semibold">{l}</div>
              </div>
            ))}
          </div>

          {/* Recent order card */}
          <div className="mt-3 rounded-2xl bg-white/10 p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-[9px] font-bold uppercase tracking-wider text-white/60">Recent order</div>
              <div className="flex items-center gap-1 text-[9px] font-bold text-emerald-400">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                </span>
                LIVE
              </div>
            </div>
            <div className="text-xs font-bold">ORD-2842</div>
            <div className="mt-0.5 text-[10px] text-white/70">2,500 L · BS-VI HSD</div>
            <div className="mt-2 h-1 overflow-hidden rounded-full bg-white/15">
              <div className="h-full w-2/3 rounded-full bg-[var(--pub-primary)]" />
            </div>
            <div className="mt-1.5 flex justify-between text-[9px] text-white/70">
              <span>Dispatched</span>
              <span className="font-bold text-white">ETA 42m</span>
            </div>
          </div>

          {/* Bottom tab bar */}
          <div className="mt-auto flex items-center justify-around rounded-2xl bg-white/5 py-2">
            {["Home", "Orders", "Wallet", "You"].map((t, i) => (
              <div key={t} className={`text-[9px] font-semibold ${i === 0 ? "text-[var(--pub-primary)]" : "text-white/50"}`}>
                {t}
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Floating rating badge — only on front variant */}
      {!isBack && (
        <div className="absolute -right-4 top-16 rotate-6 rounded-2xl bg-white px-3 py-2 text-slate-900 shadow-xl">
          <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--pub-primary)]">4.8 ★</div>
          <div className="font-display text-sm font-black leading-tight">Rated</div>
        </div>
      )}
    </div>
  );
}

/* ─────────── Contact CTA ─────────── */
const VOLUME_PERIOD_MULT = { day: 30, week: 4.33, month: 1, custom: 1 };
const VOLUME_PERIOD_LABEL = { day: "per day", week: "per week", month: "per month", custom: "custom" };

function ContactCTA({ contact }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "",
    mobile: "", email: "",
    company: "",
    volume: "", volume_period: "month",
    note: "",
  });
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const monthlyLitres = (() => {
    const n = Number(String(form.volume).replace(/[^0-9.]/g, "")) || 0;
    return Math.round(n * (VOLUME_PERIOD_MULT[form.volume_period] || 1));
  })();

  const submit = useMutation({
    mutationFn: () => {
      const fullName = `${form.first_name.trim()} ${form.last_name.trim()}`.trim();
      const periodTag = form.volume_period === "custom"
        ? `Volume: ${form.volume} L (custom)`
        : `Volume requested: ${form.volume} L ${VOLUME_PERIOD_LABEL[form.volume_period]}`;
      const combinedNotes = [periodTag, form.note].filter(Boolean).join("\n");
      return api.post("/leads/public", {
        name: fullName,
        phone: form.mobile,
        email: form.email,
        company: form.company,
        expected_monthly_litres: monthlyLitres,
        notes: combinedNotes,
        source: "landing_page",
      });
    },
    onSuccess: (r) => {
      toast.success("We'll call you back within 30 minutes.", { description: r?.data?.code ? `Reference: ${r.data.code}` : undefined });
      setForm({ first_name: "", last_name: "", mobile: "", email: "", company: "", volume: "", volume_period: "month", note: "" });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not submit. Please call us directly."),
  });

  const canSubmit = form.first_name && form.mobile && form.email && form.company && form.volume;

  return (
    <section id="contact" className="bg-[var(--pub-surface)] py-16 md:py-20">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-6 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <div className="mb-2 text-xs font-bold uppercase tracking-[0.3em] text-[var(--pub-primary)]">Get a Quote</div>
          <h2 className="font-display text-3xl font-black leading-[1.05] tracking-tight text-[var(--pub-dark)] md:text-4xl">
            READY TO
            <br />
            <span className="text-[var(--pub-primary)]">POWER UP?</span>
          </h2>
          <p className="mt-4 max-w-md text-sm text-[var(--pub-text-muted)]">
            Tell us your monthly volume — our team calls back within 30 minutes with a per-litre quote and credit-line offer.
          </p>
          <div className="mt-6 space-y-3">
            <ContactRow label="Phone" value={contact?.phone} icon="phone" />
            <ContactRow label="WhatsApp" value={contact?.whatsapp} icon="whatsapp" />
            <ContactRow label="Email" value={contact?.email} icon="email" />
            <ContactRow label="Head Office" value={contact?.address} icon="pin" />
          </div>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); submit.mutate(); }}
          className="rounded-2xl border bg-white p-6 shadow-lg md:p-7 lg:col-span-3"
          style={{ borderColor: "var(--pub-border)" }}
          data-testid="public-contact-form"
        >
          <div className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-[var(--pub-primary)]">Request callback</div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <FormField label="First name" required>
              <Input
                value={form.first_name}
                onChange={(e) => setF("first_name", e.target.value)}
                required
                className="h-10 rounded-lg border-slate-200 bg-white text-sm text-[var(--pub-text)]"
                data-testid="public-contact-first-name"
              />
            </FormField>
            <FormField label="Last name">
              <Input
                value={form.last_name}
                onChange={(e) => setF("last_name", e.target.value)}
                className="h-10 rounded-lg border-slate-200 bg-white text-sm text-[var(--pub-text)]"
                data-testid="public-contact-last-name"
              />
            </FormField>

            <FormField label="Mobile number" required>
              <Input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={form.mobile}
                onChange={(e) => setF("mobile", e.target.value)}
                required
                placeholder="+91 90000 00000"
                className="h-10 rounded-lg border-slate-200 bg-white text-sm text-[var(--pub-text)]"
                data-testid="public-contact-mobile"
              />
            </FormField>
            <FormField label="Email ID" required>
              <Input
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={(e) => setF("email", e.target.value)}
                required
                placeholder="you@company.com"
                className="h-10 rounded-lg border-slate-200 bg-white text-sm text-[var(--pub-text)]"
                data-testid="public-contact-email"
              />
            </FormField>

            <div className="md:col-span-2">
              <FormField label="Company name" required>
                <Input
                  value={form.company}
                  onChange={(e) => setF("company", e.target.value)}
                  required
                  autoComplete="organization"
                  className="h-10 rounded-lg border-slate-200 bg-white text-sm text-[var(--pub-text)]"
                  data-testid="public-contact-company"
                />
              </FormField>
            </div>

            <div className="md:col-span-2">
              <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wider text-[var(--pub-text-muted)]">
                Required volume <span className="text-[var(--pub-primary)]">*</span>
              </label>
              <div className="grid grid-cols-[1fr_auto] gap-2 sm:grid-cols-[2fr_1fr]">
                <Input
                  type="number"
                  min="0"
                  inputMode="numeric"
                  value={form.volume}
                  onChange={(e) => setF("volume", e.target.value)}
                  required
                  placeholder="e.g. 5000"
                  className="h-10 rounded-lg border-slate-200 bg-white text-sm text-[var(--pub-text)]"
                  data-testid="public-contact-volume"
                />
                <select
                  value={form.volume_period}
                  onChange={(e) => setF("volume_period", e.target.value)}
                  className="h-10 rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-[var(--pub-text)] focus:border-[var(--pub-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--pub-primary)]/20"
                  data-testid="public-contact-volume-period"
                >
                  <option value="day">L per day</option>
                  <option value="week">L per week</option>
                  <option value="month">L per month</option>
                  <option value="custom">L (custom)</option>
                </select>
              </div>
              {form.volume && (
                <div className="mt-1 text-[11px] text-[var(--pub-text-muted)]">
                  {form.volume_period === "custom"
                    ? `Stored as ${monthlyLitres.toLocaleString("en-IN")} L`
                    : `≈ ${monthlyLitres.toLocaleString("en-IN")} L per month`}
                </div>
              )}
            </div>

            <div className="md:col-span-2">
              <FormField label="Notes (optional)">
                <Textarea
                  rows={2}
                  value={form.note}
                  onChange={(e) => setF("note", e.target.value)}
                  placeholder="Delivery location, timing, any special requirements…"
                  className="rounded-lg border-slate-200 bg-white text-sm text-[var(--pub-text)]"
                />
              </FormField>
            </div>

            <div className="md:col-span-2 flex flex-col gap-2 pt-1">
              <Button
                type="submit"
                disabled={submit.isPending || !canSubmit}
                className="h-12 w-full rounded-full bg-[var(--pub-primary)] font-semibold text-white shadow-md shadow-[var(--pub-primary)]/30 hover:bg-[var(--pub-primary-hover)]"
                data-testid="public-contact-submit"
              >
                {submit.isPending ? "Submitting…" : "Submit Request"} <ArrowRight size={16} className="ml-2" />
              </Button>
              <p className="text-center text-[10px] text-[var(--pub-text-muted)]">
                By submitting you agree to be contacted about your enquiry. We never share your details with third parties.
              </p>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}

function FormField({ label, required, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wider text-[var(--pub-text-muted)]">
        {label} {required && <span className="text-[var(--pub-primary)]">*</span>}
      </label>
      {children}
    </div>
  );
}

function ContactRow({ label, value, icon }) {
  if (!value) return null;
  const Icon =
    icon === "shield"
      ? ShieldCheck
      : icon === "pin"
      ? MapPin
      : icon === "email"
      ? Mail
      : icon === "whatsapp"
      ? MessageSquare
      : icon === "phone"
      ? Phone
      : CheckCircle2;
  return (
    <div className="flex items-start gap-3">
      <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full bg-[var(--pub-primary)]/12 text-[var(--pub-primary)]">
        <Icon size={16} />
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-[var(--pub-text-muted)]">{label}</div>
        <div className="mt-0.5 text-sm font-medium text-[var(--pub-text)]">{value}</div>
      </div>
    </div>
  );
}

/* ─────────── Social media platforms ─────────── */
const SOCIAL_PLATFORMS = [
  { key: "facebook",  label: "Facebook",  Icon: Facebook,       hoverBg: "hover:bg-[#1877F2]" },
  { key: "twitter",   label: "X (Twitter)", Icon: Twitter,      hoverBg: "hover:bg-black" },
  { key: "instagram", label: "Instagram", Icon: Instagram,      hoverBg: "hover:bg-gradient-to-br hover:from-[#F58529] hover:via-[#DD2A7B] hover:to-[#8134AF]" },
  { key: "linkedin",  label: "LinkedIn",  Icon: Linkedin,       hoverBg: "hover:bg-[#0A66C2]" },
  { key: "youtube",   label: "YouTube",   Icon: Youtube,        hoverBg: "hover:bg-[#FF0000]" },
  { key: "whatsapp",  label: "WhatsApp",  Icon: MessageSquare,  hoverBg: "hover:bg-[#25D366]" },
  { key: "telegram",  label: "Telegram",  Icon: Send,           hoverBg: "hover:bg-[#26A5E4]" },
];

function SocialRow({ social }) {
  if (!social || social.enabled === false) return null;
  const active = SOCIAL_PLATFORMS.filter((p) => {
    const cfg = social[p.key];
    return cfg && cfg.enabled && (cfg.url || "").trim();
  });
  if (active.length === 0) return null;
  return (
    <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="footer-social">
      {active.map(({ key, label, Icon, hoverBg }) => {
        const url = social[key].url;
        return (
          <a
            key={key}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            title={label}
            className={`grid h-9 w-9 place-items-center rounded-full bg-white/10 text-white/80 transition-all hover:text-white hover:scale-110 ${hoverBg}`}
            data-testid={`footer-social-${key}`}
          >
            <Icon size={15} />
          </a>
        );
      })}
    </div>
  );
}

/* ─────────── Footer ─────────── */
function Footer({ footer, brand, social }) {
  if (!footer) return null;
  return (
    <footer className="bg-[var(--pub-dark)] py-20 text-white">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid grid-cols-2 gap-10 md:grid-cols-5">
          <div className="col-span-2">
            <div className="flex items-center gap-2.5">
              <div className="grid h-11 w-11 place-items-center rounded-2xl bg-[var(--pub-primary)] text-white shadow-lg">
                <Fuel size={20} strokeWidth={2.5} />
              </div>
              <div className="font-display text-2xl font-black tracking-tight">{brand?.name}</div>
            </div>
            <p className="mt-4 max-w-xs text-sm text-white/60">{footer.tagline}</p>
            <SocialRow social={social} />
          </div>
          {(footer.columns || []).map((col) => (
            <div key={col.title}>
              <div className="mb-4 text-xs font-bold uppercase tracking-widest text-[var(--pub-primary)]">{col.title}</div>
              <ul className="space-y-2.5">
                {(col.links || []).map((l) => (
                  <li key={l.label}>
                    <a href={l.href} className="text-sm text-white/70 transition-colors hover:text-white">
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-8 text-xs text-white/50 md:flex-row md:items-center">
          <div>{footer.copyright}</div>
          <div className="flex flex-wrap gap-4 sm:gap-6">
            <Link to="/login" className="transition-colors hover:text-white">
              Client Login
            </Link>
            <Link to="/privacy" className="transition-colors hover:text-white" data-testid="footer-privacy">
              Privacy Policy
            </Link>
            <Link to="/terms" className="transition-colors hover:text-white" data-testid="footer-terms">
              Terms &amp; Conditions
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
